/* ==========================================================================
   Skunx Escape Rooms — theme behaviour
   Independent pieces, all no-ops when their markup is absent:
     1. <sx-meters>           animate bars on first scroll into view
     2. <sx-edition-switcher> show selection that drives the whole page
     3. <sx-lightbox>         the photo row, enlarged
     4. <sx-booking-embed>    lazy-load the external booking script
     5. <sx-signup-dialog>    the announcement bar's e-mail pop-up
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     1. Meters
     Bars sit at width:0 until their row scrolls into view, then fill with a
     90ms stagger.

     Two traps this has to stay clear of, both of which leave bars stuck empty:

     a) Do NOT mark a row "already seen" with a DOM attribute and unobserve it.
        A row that happened to be off-screen — or inside a display:none panel —
        during a re-render would then never be observed again.
     b) Do NOT keep the filled state only in the DOM. It is wiped the moment the
        visitor works the show slider or the language switch, which is the main
        interaction on this page. The Set below is the source of truth; the DOM
        is re-stamped from it after every render.
     ------------------------------------------------------------------------ */

  class SxMetersElement extends HTMLElement {
    connectedCallback() {
      this.filled = new Set();
      this.restamp = this.restamp.bind(this);

      this.collect();
      if (!this.meters.length) return;

      // Without IntersectionObserver, or with reduced motion, show them filled.
      if (!('IntersectionObserver' in window) || reduceMotion) {
        this.meters.forEach((_meter, index) => this.filled.add(index));
        this.stamp(false);
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          var earned = false;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            var index = this.meters.indexOf(entry.target);
            if (index === -1 || this.filled.has(index)) return;
            this.filled.add(index);
            earned = true;
          });
          if (earned) this.stamp(true);
        },
        { threshold: 0.2 }
      );

      this.observeAll();

      // A re-render swaps the rows out from under us: re-collect, put back the
      // fills already earned, and observe whatever is new.
      this.mutations = new MutationObserver(this.restamp);
      this.mutations.observe(this, { childList: true, subtree: true });

      // Switching show unhides a panel that had no layout a moment ago.
      document.addEventListener('sx:show-change', this.restamp);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.mutations) this.mutations.disconnect();
      document.removeEventListener('sx:show-change', this.restamp);
    }

    collect() {
      this.meters = Array.from(this.querySelectorAll('.sx-meter'));
    }

    restamp() {
      this.collect();
      this.stamp(false);
      this.observeAll();
    }

    observeAll() {
      if (!this.observer) return;
      this.meters.forEach((meter) => this.observer.observe(meter));
    }

    /**
     * Write the earned fills into the DOM.
     * @param {boolean} stagger  add the 90ms-per-bar delay
     */
    stamp(stagger) {
      this.meters.forEach((meter, index) => {
        if (!this.filled.has(index)) return;
        meter.style.setProperty('--sx-meter-delay', stagger === true ? index * 90 + 'ms' : '0ms');
        meter.setAttribute('data-fill', '');
      });
    }
  }

  if (!customElements.get('sx-meters')) {
    customElements.define('sx-meters', SxMetersElement);
  }

  /* ------------------------------------------------------------------------
     2. Show switcher
     Both shows are already in the DOM; selecting one toggles visibility
     rather than refetching, so the swap is instant and the external booking
     embed is never torn down. Broadcasts `sx:show-change` on `document`
     so sections further down the page (the comparison table) can follow.
     ------------------------------------------------------------------------ */

  class SxEditionSwitcher extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('[data-sx-edition-button]'));
      if (!this.buttons.length) return;

      this.updateUrl = this.dataset.updateUrl !== 'false';

      this.buttons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.select(button.dataset.variantId, true);
        });
      });

      // A variant in the URL wins over the server-rendered default, so a
      // shared /products/…?variant=123 link opens on the right show.
      const fromUrl = new URLSearchParams(window.location.search).get('variant');
      const initial =
        (fromUrl && this.buttons.some((b) => b.dataset.variantId === fromUrl) && fromUrl) ||
        this.dataset.selectedVariantId ||
        this.buttons[0].dataset.variantId;

      this.select(initial, false);

      // Keep the page honest when the visitor uses back/forward.
      window.addEventListener('popstate', () => {
        const id = new URLSearchParams(window.location.search).get('variant');
        if (id && this.buttons.some((b) => b.dataset.variantId === id)) {
          this.select(id, false);
        }
      });
    }

    /**
     * @param {string}  variantId
     * @param {boolean} pushUrl   write ?variant= into the address bar
     */
    select(variantId, pushUrl) {
      if (!variantId || variantId === this.current) return;
      this.current = variantId;

      var pos = null;

      this.buttons.forEach((button) => {
        const on = button.dataset.variantId === variantId;
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
        button.classList.toggle('is-active', on);
        // The slider halves read their own state off data-on; the plain button
        // row (three shows or more) uses .is-active. Both are set either way,
        // so one code path serves both layouts.
        button.dataset.on = on ? '1' : '0';
        if (on && button.dataset.pos) pos = button.dataset.pos;
      });

      if (pos) this.slide(pos);

      if (pushUrl && this.updateUrl && window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      }

      document.dispatchEvent(
        new CustomEvent('sx:show-change', {
          detail: {
            variantId: variantId,
            productId: this.dataset.productId || null,
            pos: pos,
          },
        })
      );
    }

    /**
     * Move the thumb and repaint the page tone.
     * @param {string} pos  'plus16' (The Ritual) or 'familie' (The Lost Family)
     */
    slide(pos) {
      var track = this.querySelector('[data-toggle]');
      if (track) track.dataset.pos = pos;

      var thumb = this.querySelector('[data-thumb]');
      if (thumb) thumb.dataset.pos = pos;

      applyTone(pos);
    }
  }

  /* The page tone follows the chosen show. Written on :root so any section can
     opt in with var(--tone-*); each rule keeps a static fallback, so the page is
     correctly coloured before this ever runs and with JS off entirely. */
  var TONES = {
    familie: {
      '--tone-deep': '#12292E',
      '--tone-soft': '#EDE3D0',
      '--tone-edge': '#F5C877',
      '--showdot': '#E8A33D',
    },
    plus16: {
      '--tone-deep': '#170709',
      '--tone-soft': '#E9DBD3',
      '--tone-edge': '#FF8A73',
      '--showdot': '#B0392C',
    },
  };

  function applyTone(pos) {
    var tone = TONES[pos];
    if (!tone) return;
    var root = document.documentElement;
    Object.keys(tone).forEach(function (property) {
      root.style.setProperty(property, tone[property]);
    });
    root.dataset.sxTone = pos;
  }

  if (!customElements.get('sx-edition-switcher')) {
    customElements.define('sx-edition-switcher', SxEditionSwitcher);
  }

  /* Any element carrying data-sx-edition-panel="<variant id>" is shown only
     while that show is selected. Used by the poster, the mood line, the show
     pill, the sidebar confirmation strip and the comparison column highlight. */
  document.addEventListener('sx:show-change', function (event) {
    var id = event.detail.variantId;

    document.querySelectorAll('[data-sx-edition-panel]').forEach(function (panel) {
      panel.hidden = panel.dataset.sxEditionPanel !== id;
    });

    document.querySelectorAll('[data-sx-edition-column]').forEach(function (column) {
      column.classList.toggle('is-selected', column.dataset.sxEditionColumn === id);
    });
  });

  /* Links that preselect an edition from elsewhere on the site (home cards,
     collection cards, the comparison CTAs). They are ordinary hrefs to the
     product page, so they still work with JS off; when the switcher is on the
     same page we intercept and switch in place. */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-sx-edition-link]');
    if (!trigger) return;

    var switcher = document.querySelector('sx-edition-switcher');
    if (!switcher) return; // different page — let the link navigate

    var id = trigger.dataset.sxEditionLink;
    if (!Array.from(switcher.querySelectorAll('[data-sx-edition-button]')).some(function (b) {
      return b.dataset.variantId === id;
    })) {
      return;
    }

    event.preventDefault();
    switcher.select(id, true);
    switcher.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  });

  /* ------------------------------------------------------------------------
     3. Lightbox
     Wraps a group of [data-sx-photo] buttons around one <dialog>. Uses
     showModal(), so the focus trap, Esc-to-close and the inert backdrop come
     from the browser instead of hand-rolled JS. Keyboard arrows step through
     the set; on touch, a horizontal swipe does the same.
     ------------------------------------------------------------------------ */

  class SxLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-sx-lb-dialog]');
      this.image = this.querySelector('[data-sx-lb-img]');
      this.caption = this.querySelector('[data-sx-lb-caption]');
      this.counter = this.querySelector('[data-sx-lb-counter]');
      this.photos = Array.from(this.querySelectorAll('[data-sx-photo]'));

      // <dialog> without showModal support: leave the thumbs as plain images.
      if (!this.dialog || !this.photos.length || typeof this.dialog.showModal !== 'function') {
        this.photos.forEach((b) => b.removeAttribute('data-sx-photo'));
        return;
      }

      if (this.photos.length < 2) this.dialog.setAttribute('data-single', '');

      this.photos.forEach((button, index) => {
        button.addEventListener('click', () => this.open(index));
      });

      this.querySelector('[data-sx-lb-close]').addEventListener('click', () => this.dialog.close());
      this.querySelector('[data-sx-lb-prev]').addEventListener('click', () => this.step(-1));
      this.querySelector('[data-sx-lb-next]').addEventListener('click', () => this.step(1));

      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
      });

      // Clicking the backdrop closes: the dialog fills its own box, so a click
      // landing on the dialog element itself is outside the stage.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      // Return focus to the thumb that opened it.
      this.dialog.addEventListener('close', () => {
        if (this.opener) this.opener.focus();
      });

      this.bindSwipe();
    }

    bindSwipe() {
      var startX = null;
      var startY = null;
      this.dialog.addEventListener(
        'touchstart',
        (event) => {
          startX = event.changedTouches[0].clientX;
          startY = event.changedTouches[0].clientY;
        },
        { passive: true }
      );
      this.dialog.addEventListener(
        'touchend',
        (event) => {
          if (startX === null) return;
          const dx = event.changedTouches[0].clientX - startX;
          const dy = event.changedTouches[0].clientY - startY;
          // Horizontal intent only, so a vertical scroll never flips the photo.
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) this.step(dx < 0 ? 1 : -1);
          startX = null;
          startY = null;
        },
        { passive: true }
      );
    }

    open(index) {
      this.opener = this.photos[index];
      this.show(index);
      this.dialog.showModal();
    }

    step(delta) {
      const next = (this.index + delta + this.photos.length) % this.photos.length;
      this.show(next);
    }

    show(index) {
      this.index = index;
      const button = this.photos[index];
      const thumb = button.querySelector('img');
      const caption = button.dataset.sxPhotoCaption || (thumb && thumb.alt) || '';

      this.image.src = button.dataset.sxPhotoFull || (thumb && thumb.currentSrc) || '';
      this.image.alt = caption;
      this.caption.textContent = caption;
      if (this.counter) this.counter.textContent = index + 1 + ' / ' + this.photos.length;
    }
  }

  if (!customElements.get('sx-lightbox')) {
    customElements.define('sx-lightbox', SxLightbox);
  }

  /* ------------------------------------------------------------------------
     4. Booking embed
     The heaviest third-party script on the site. It must never block LCP, so
     we inject it only when the sidebar nears the viewport, or on the first
     interaction with the card — whichever comes first.
     ------------------------------------------------------------------------ */

  class SxBookingEmbed extends HTMLElement {
    connectedCallback() {
      this.template = this.querySelector('template[data-sx-booking-code]');
      if (!this.template || !this.template.innerHTML.trim()) return;

      this.loaded = false;
      this.load = this.load.bind(this);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.load();
            });
          },
          { rootMargin: '400px 0px' }
        );
        this.observer.observe(this);
      } else {
        this.load();
      }

      this.addEventListener('click', this.load, { once: true });
      this.addEventListener('focusin', this.load, { once: true });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    load() {
      if (this.loaded) return;
      this.loaded = true;
      if (this.observer) this.observer.disconnect();

      const slot = this.querySelector('[data-sx-booking-slot]');
      if (!slot) return;

      const fragment = this.template.content.cloneNode(true);

      // widget.js only frames its agenda when the page is the top window. In
      // the theme editor the page sits in an iframe itself, and the agenda is
      // then rendered straight into the page, sized on the viewport (100dvh):
      // the full height of the screen, deaf to the card's height setting. So
      // whenever widget.js would go inline, build the frame it builds on the
      // live site; the editor then shows what a visitor gets.
      if (window.self !== window.top) this.frameOnlineafspraken(fragment);

      // Scripts inside a <template> are inert; re-create them so they run.
      fragment.querySelectorAll('script').forEach((original) => {
        const script = document.createElement('script');
        Array.from(original.attributes).forEach((attribute) => {
          script.setAttribute(attribute.name, attribute.value);
        });
        script.textContent = original.textContent;
        original.parentNode.replaceChild(script, original);
      });

      slot.innerHTML = '';
      slot.appendChild(fragment);
      this.setAttribute('data-loaded', 'true');
    }

    // Swaps the onlineafspraken script for the iframe widget.js itself would
    // create: same /book/{key}/{widget}/{mode}/{buttonOnMobile} address, keys
    // read from the script or the root, as widget.js reads them. Any other
    // embed code is left alone.
    frameOnlineafspraken(fragment) {
      const root = fragment.querySelector('#_oa_widget_root');
      const script = Array.from(fragment.querySelectorAll('script[src]')).find(
        (candidate) =>
          new URL(candidate.getAttribute('src'), window.location.href).pathname === '/build/widget/widget.js'
      );
      if (!root || !script) return;

      const data = script.dataset.apiKey ? script.dataset : root.dataset;
      if (!data.apiKey || !data.widgetId) return;

      const frame = document.createElement('iframe');
      frame.id = '_oa_widget_frame_' + data.widgetId;
      frame.src = [
        new URL(script.getAttribute('src'), window.location.href).origin,
        'book',
        data.apiKey,
        data.widgetId,
        data.mode || 'default',
        data.buttonOnMobile || 'false',
      ].join('/');
      frame.style.border = 'none';
      frame.style.backgroundColor = 'transparent';

      script.remove();
      root.appendChild(frame);
    }
  }

  if (!customElements.get('sx-booking-embed')) {
    customElements.define('sx-booking-embed', SxBookingEmbed);
  }

  /* ------------------------------------------------------------------------
     5. Sign-up dialog
     The announcement bar's link opens a small e-mail form instead of
     navigating. Same <dialog> + showModal() as the lightbox, so the focus
     trap, Esc and the inert backdrop come from the browser.

     The one awkward part is the round trip. Shopify's customer form posts and
     reloads the page, and it reports `posted_successfully?` on EVERY customer
     form present — so the footer's sign-up would otherwise reopen this dialog
     with a success message nobody asked for. A key in sessionStorage, written
     the moment this form submits, is what tells the two apart on the way back.
     ------------------------------------------------------------------------ */

  var SIGNUP_KEY = 'sx-signup-pending';

  /* Long enough to survive an hCaptcha challenge, short enough that a note left
     behind by someone who walked away cannot greet them an hour later. */
  var SIGNUP_MAX_AGE = 10 * 60 * 1000;

  class SxSignupDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('[data-sx-signup-panel]');
      this.done = this.querySelector('[data-sx-signup-done]');
      this.error = this.querySelector('[data-sx-signup-error]');
      if (!this.dialog) return;

      // The trigger lives in the bar, outside this element, and there is only
      // ever one bar — so the first instance claims the document listener.
      if (!SxSignupDialog.bound) {
        SxSignupDialog.bound = this;
        document.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-sx-signup-open]');
          if (!trigger) return;
          event.preventDefault();
          SxSignupDialog.bound.open(trigger);
        });
      }

      this.querySelectorAll('[data-sx-signup-close]').forEach((button) => {
        button.addEventListener('click', () => this.dialog.close());
      });

      // Clicking the backdrop closes: the panel fills the dialog's own box, so
      // a click landing on the dialog element itself is outside the panel.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      this.dialog.addEventListener('close', () => {
        if (this.closeTimer) {
          clearTimeout(this.closeTimer);
          this.closeTimer = null;
        }
        this.reset();
        if (this.opener && this.opener.isConnected) this.opener.focus();
      });

      if (this.form) {
        /* The form posts the ordinary way, on purpose.

           Shopify's hCaptcha wires itself onto customer forms and expects a
           valid token with the submission. Posting in the background from
           script skips that wiring, so the store starts treating sign-ups as
           bot traffic and sends people to /challenge. What actually annoyed
           anyone was not the reload but losing their place on the page, and
           that is fixable without going around the spam protection. */
        this.form.addEventListener('submit', () => {
          try {
            sessionStorage.setItem(
              SIGNUP_KEY,
              JSON.stringify({ scroll: window.scrollY || 0, at: Date.now() })
            );
          } catch (error) {
            // Private mode, blocked storage. The sign-up still goes through;
            // only the scroll position and the tick are lost.
          }
        });

        this.restore();
      }
    }

    restore() {
      var pending = null;
      try {
        const stored = sessionStorage.getItem(SIGNUP_KEY);
        if (!stored) return;
        pending = JSON.parse(stored);
      } catch (error) {
        return;
      }
      if (!pending) return;

      const clear = () => {
        try {
          sessionStorage.removeItem(SIGNUP_KEY);
        } catch (error) {
          // Nothing to clean up.
        }
      };

      if (!pending.at || Date.now() - pending.at > SIGNUP_MAX_AGE) {
        clear();
        return;
      }

      /* The note says this browser submitted the form; these say the page in
         front of us is the answer to it.

         When neither is set we are somewhere in between — hCaptcha's /challenge
         page is exactly that — so the note is deliberately left alone. Clearing
         it here is what made the tick disappear after a challenge: by the time
         the real answer arrived, there was nothing left to act on. */
      const ok = this.dataset.postedOk === 'true';
      const failed = this.dataset.postedError === 'true';
      if (!ok && !failed) return;

      clear();

      // Back to where they were reading. Twice, because artwork further up the
      // page can still land after this runs and push everything down.
      const y = parseInt(pending.scroll, 10) || 0;
      window.scrollTo(0, y);
      window.addEventListener('load', () => window.scrollTo(0, y), { once: true });

      this.open();
      if (failed) this.fail();
      else this.succeed();
    }

    fail(message) {
      if (!this.error) return;
      if (message) this.error.textContent = message;
      this.error.hidden = false;

      const field = this.dialog.querySelector('input[type="email"]');
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        field.focus();
      }
    }

    succeed() {
      if (this.panel) this.panel.hidden = true;
      if (this.done) this.done.hidden = false;

      const delay = parseInt(this.dataset.closeDelay, 10);
      if (!delay || delay < 1) return;

      this.closeTimer = setTimeout(() => {
        if (this.dialog.open) this.dialog.close();
      }, delay * 1000);
    }

    /* Back to the form, but only after a confirmation: closing a half-typed
       address should not wipe it. */
    reset() {
      if (!this.done || this.done.hidden) return;

      this.done.hidden = true;
      if (this.panel) this.panel.hidden = false;
      if (this.form) this.form.reset();

      const button = this.form && this.form.querySelector('[type="submit"]');
      if (button) button.disabled = false;
    }

    open(trigger) {
      this.opener = trigger || null;
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      const field = this.dialog.querySelector('input[type="email"]');
      if (field && !field.value && this.panel && !this.panel.hidden) field.focus();
    }
  }

  if (!customElements.get('sx-signup-dialog')) {
    customElements.define('sx-signup-dialog', SxSignupDialog);
  }
})();
